import {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import {
  buildSiteComplianceCheckpointExportRows,
  useSiteComplianceEvaluationQuery,
} from '@/domains/siteCompliance';
import type { EvaluationCheckpoint } from '@/domains/evaluation/evaluation.types';
import type { EvaluationView } from '@/domains/checkpoint/checkpointViewAccess';
import type { Application } from '@/types';
import { exportToCsv } from '@/lib/utils';
import { useMobileRouteLoading } from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';

import {
  buildSiteComplianceDetailsPath,
  buildSiteComplianceEvaluationPath,
  buildSiteComplianceListPath,
} from '../../siteComplianceRouting';

interface UseSiteEvaluationControllerResult {
  activeCheckpoint: EvaluationCheckpoint | null;
  activeView: EvaluationView;
  application: Application | null;
  availableViews: EvaluationView[];
  emptyStateMessage: string | null;
  errorMessage: string | null;
  evaluationId: string;
  evaluation: NonNullable<
    ReturnType<typeof useSiteComplianceEvaluationQuery>['data']
  >['evaluation'] | null;
  handleBack: () => void;
  handleCheckpointSelect: (checkpoint: EvaluationCheckpoint) => void;
  handleExportEvaluation: () => Promise<void>;
  handleViewChange: (view: EvaluationView) => void;
  isLoading: boolean;
}

export const useSiteEvaluationController =
  (): UseSiteEvaluationControllerResult => {
    const location = useLocation();
    const navigate = useNavigate();
    const { evaluationId = '' } = useParams();
    const [searchParams] = useSearchParams();
    const { showToast } = useToast();
    const {
      data: evaluationDetails,
      error,
      isFetching,
      isError,
      isLoading,
    } = useSiteComplianceEvaluationQuery(evaluationId);

    useMobileRouteLoading(isLoading || isFetching);

    const isNotFound = useMemo(
      () =>
        Boolean(
          error &&
            typeof error === 'object' &&
            'response' in error &&
            (error as { response?: { status?: number } }).response?.status ===
              404,
        ),
      [error],
    );

    useEffect(() => {
      if (isError && !isNotFound) {
        console.error('Site Compliance evaluation details failed to load', {
          error,
          evaluationId,
          jobId: null,
          siteId: evaluationDetails?.site.siteId ?? null,
        });
        showToast('error', {
          title: 'Error loading evaluation',
          message: 'Site Compliance evaluation details could not be loaded.',
        });
      }
    }, [error, evaluationDetails?.site.siteId, evaluationId, isError, isNotFound, showToast]);

    const availableViews = evaluationDetails?.availableViews ?? ['default'];
    const requestedView = searchParams.get('view');
    const activeView = useMemo<EvaluationView>(() => {
      if (
        requestedView &&
        (requestedView === 'default' ||
          requestedView === 'dr' ||
          requestedView === 'network' ||
          requestedView === 'nova') &&
        availableViews.includes(requestedView)
      ) {
        return requestedView;
      }

      return availableViews[0] ?? 'default';
    }, [availableViews, requestedView]);

    const activeCheckpoint = useMemo(() => {
      const checkpoints = evaluationDetails?.evaluation.checkpoints ?? [];
      const wantedId = searchParams.get('checkpoint');
      const wantedVersion = searchParams.get('checkpointVersion');

      if (!wantedId) {
        return checkpoints[0] ?? null;
      }

      const matchingById = checkpoints.filter(
        (checkpoint) => checkpoint.metadata.id === wantedId,
      );

      if (!matchingById.length) {
        return checkpoints[0] ?? null;
      }

      if (!wantedVersion) {
        return matchingById[0];
      }

      return (
        matchingById.find(
          (checkpoint) =>
            `${checkpoint.metadata.checkpointVersion ?? checkpoint.metadata.version ?? ''}` ===
            wantedVersion,
        ) ?? matchingById[0]
      );
    }, [evaluationDetails?.evaluation.checkpoints, searchParams]);

    const handleBack = useCallback(() => {
      const siteId =
        evaluationDetails?.site?.siteId ?? evaluationId.split('!')[0];

      if (siteId) {
        navigate(buildSiteComplianceDetailsPath(location.pathname, siteId));
        return;
      }

      navigate(buildSiteComplianceListPath(location.pathname));
    }, [evaluationDetails?.site, evaluationId, location.pathname, navigate]);

    const navigateWithSearchParams = useCallback(
      (nextSearchParams: URLSearchParams) => {
        if (!evaluationId) {
          return;
        }

        const nextSearch = nextSearchParams.toString();
        const path = buildSiteComplianceEvaluationPath(location.pathname, evaluationId);

        navigate(nextSearch ? `${path}?${nextSearch}` : path, {
          replace: true,
        });
      },
      [evaluationId, location.pathname, navigate],
    );

    const handleCheckpointSelect = useCallback(
      (checkpoint: EvaluationCheckpoint) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('checkpoint', checkpoint.metadata.id);

        const version =
          checkpoint.metadata.checkpointVersion ?? checkpoint.metadata.version;

        if (version === null || version === undefined || version === '') {
          nextSearchParams.delete('checkpointVersion');
        } else {
          nextSearchParams.set('checkpointVersion', `${version}`);
        }

        navigateWithSearchParams(nextSearchParams);
      },
      [navigateWithSearchParams, searchParams],
    );

    const handleViewChange = useCallback(
      (view: EvaluationView) => {
        const nextSearchParams = new URLSearchParams(searchParams);

        if (view === 'default') {
          nextSearchParams.delete('view');
        } else {
          nextSearchParams.set('view', view);
        }

        navigateWithSearchParams(nextSearchParams);
      },
      [navigateWithSearchParams, searchParams],
    );

    const handleExportEvaluation = useCallback(async () => {
      if (!evaluationDetails?.evaluation) {
        return;
      }

      try {
        await exportToCsv({
          fileName: `site-compliance-checkpoints-${evaluationDetails.site.siteId}-${evaluationDetails.evaluation.id}`,
          options: {},
          rows: buildSiteComplianceCheckpointExportRows({
            evaluation: evaluationDetails.evaluation,
          }),
        });

        showToast('success', {
          message: 'Checkpoint CSV exported successfully',
        });
      } catch (error) {
        console.error('Site Compliance checkpoint CSV export failed', {
          error,
          evaluationId: evaluationDetails.evaluation.id,
          jobId: null,
          siteId: evaluationDetails.site.siteId,
        });
        showToast('error', {
          title: 'Error exporting checkpoints',
          message: 'An error occurred while exporting the checkpoint CSV.',
        });
      }
    }, [evaluationDetails, showToast]);

    return {
      activeCheckpoint,
      activeView,
      application: evaluationDetails?.evaluation.metadata ?? null,
      availableViews,
      emptyStateMessage:
        !isLoading && !isFetching && isNotFound
          ? 'Evaluation not found'
          : null,
      errorMessage:
        !isLoading && !isFetching && isError && !isNotFound
          ? 'Site Compliance evaluation details could not be loaded.'
          : null,
      evaluation: evaluationDetails?.evaluation ?? null,
      evaluationId,
      handleBack,
      handleCheckpointSelect,
      handleExportEvaluation,
      handleViewChange,
      isLoading: isLoading || isFetching,
    };
  };
